import { URL_PREFIX } from '../constants/api';
import { APIError } from './errors';

// API Response Types (matching refactored backend schema)
export interface StreamsByDspTotalsResponse {
  name: string;
  streams_tw: number;
  streams_fytd: number;
  streams_fytd_prev: number;
}

export interface StreamsByDspSeriesResponse {
  date: string;
  name: string;
  streams: number;
}

// Transformed Types for Frontend
export interface StreamsByDspTableItem {
  customerName: string;
  thisWeekStreams: number;
  fytdStreams: number;
  prevFytdStreams: number;
  fytdChange: number;
  fytdPercent: number;
}

export interface StreamsByDspChartItem {
  date: string;
  spotify: number;
  youtube: number;
  appleMusic: number;
  amazon: number;
  other: number;
  [key: string]: string | number;
}

// DSP name mapping from API response to our keys
const DSP_NAME_MAP: Record<string, string> = {
  Spotify: 'spotify',
  Youtube: 'youtube',
  'Apple Music': 'appleMusic',
  Amazon: 'amazon',
  Other: 'other',
};

/**
 * Fetch streams by DSP totals data (for table)
 */
export const getStreamsByDspTable = async (): Promise<
  StreamsByDspTableItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/streams-by-dsp-totals`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch streams by DSP totals: ${response.statusText}`,
        response.status,
      );
    }

    const data = (await response.json()) as StreamsByDspTotalsResponse[];

    return data.map((item) => {
      const fytdChange =
        item.streams_fytd_prev > 0
          ? ((item.streams_fytd - item.streams_fytd_prev) /
              item.streams_fytd_prev) *
            100
          : 0;

      return {
        customerName: item.name,
        thisWeekStreams: item.streams_tw,
        fytdStreams: item.streams_fytd,
        prevFytdStreams: item.streams_fytd_prev,
        fytdChange,
        fytdPercent: 0, // Will be calculated after totals are computed
      };
    });
  } catch (error) {
    console.warn('Failed to fetch streams by DSP totals:', error);

    throw error;
  }
};

/**
 * Fetch streams by DSP series data (for chart)
 */
export const getStreamsByDspChart = async (): Promise<
  StreamsByDspChartItem[]
> => {
  try {
    const url = `${URL_PREFIX}/api/v1/bi-reports/streams-by-dsp-series`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      throw new APIError(
        `Failed to fetch streams by DSP series: ${response.statusText}`,
        response.status,
      );
    }

    const data = (await response.json()) as StreamsByDspSeriesResponse[];

    // Transform API response to chart format
    // API returns flat list: [{ date, name, streams }, ...]
    // We need to pivot to date-based objects with DSP values

    // First, collect all unique dates and build a map
    const dateMap = new Map<
      string,
      {
        rawDate: Date;
        spotify: number;
        youtube: number;
        appleMusic: number;
        amazon: number;
        other: number;
      }
    >();

    data.forEach((item) => {
      const dspKey = DSP_NAME_MAP[item.name];

      if (!dspKey) {
        return; // Skip unknown DSPs
      }

      const rawDate = new Date(item.date);
      const dateStr = rawDate.toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: '2-digit',
      });

      if (!dateMap.has(dateStr)) {
        dateMap.set(dateStr, {
          rawDate,
          spotify: 0,
          youtube: 0,
          appleMusic: 0,
          amazon: 0,
          other: 0,
        });
      }

      const dateEntry = dateMap.get(dateStr)!;

      dateEntry[dspKey as keyof Omit<typeof dateEntry, 'rawDate'>] =
        item.streams;
    });

    // Convert map to array sorted by date
    const chartData: StreamsByDspChartItem[] = [];

    // Sort dates chronologically using the raw date
    const sortedEntries = Array.from(dateMap.entries()).sort(
      (a, b) => a[1].rawDate.getTime() - b[1].rawDate.getTime(),
    );

    // Get last 12 months of data (approximately 52 weeks)
    const last12MonthsEntries = sortedEntries.slice(-52);

    last12MonthsEntries.forEach(([date, values]) => {
      chartData.push({
        date,
        spotify: values.spotify,
        youtube: values.youtube,
        appleMusic: values.appleMusic,
        amazon: values.amazon,
        other: values.other,
      });
    });

    return chartData;
  } catch (error) {
    console.warn('Failed to fetch streams by DSP series:', error);

    throw error;
  }
};

/**
 * Calculate totals and percentages from table data
 */
export const calculateTableTotals = (
  data: StreamsByDspTableItem[],
): {
  items: StreamsByDspTableItem[];
  totals: StreamsByDspTableItem;
} => {
  const totalThisWeekStreams = data.reduce(
    (sum, item) => sum + item.thisWeekStreams,
    0,
  );
  const totalFytdStreams = data.reduce(
    (sum, item) => sum + item.fytdStreams,
    0,
  );
  const totalPrevFytdStreams = data.reduce(
    (sum, item) => sum + item.prevFytdStreams,
    0,
  );

  // Calculate percentages for each item
  const itemsWithPercent = data.map((item) => ({
    ...item,
    fytdPercent:
      totalFytdStreams > 0
        ? Math.round((item.fytdStreams / totalFytdStreams) * 100)
        : 0,
  }));

  // Calculate totals row
  const totalFytdChange =
    totalPrevFytdStreams > 0
      ? ((totalFytdStreams - totalPrevFytdStreams) / totalPrevFytdStreams) * 100
      : 0;

  const totals: StreamsByDspTableItem = {
    customerName: 'Total',
    thisWeekStreams: totalThisWeekStreams,
    fytdStreams: totalFytdStreams,
    prevFytdStreams: totalPrevFytdStreams,
    fytdChange: totalFytdChange,
    fytdPercent: 100,
  };

  return {
    items: itemsWithPercent,
    totals,
  };
};
